import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, TextField, IconButton, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody,
  Alert, CircularProgress, ToggleButtonGroup, ToggleButton,
  MenuItem, Select, InputLabel, FormControl, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  QrCode2 as QrIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { adminAPI } from '../services/api';

const SOURCE_PRESETS = ['facebook', 'google', 'flyer', 'radio', 'referral', 'email', 'other'];
const MEDIUM_PRESETS = ['paid', 'print', 'offline', 'email', 'organic'];
const DEFAULT_BASE_URL = 'https://get.secondclear.com';

const sanitize = (val) => {
  if (!val) return '';
  return val.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '');
};

const CampaignLinks = () => {
  const [mode, setMode] = useState('single');
  const [links, setLinks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Form state
  const [baseUrl, setBaseUrl] = useState(DEFAULT_BASE_URL);
  const [source, setSource] = useState('');
  const [customSource, setCustomSource] = useState('');
  const [medium, setMedium] = useState('');
  const [customMedium, setCustomMedium] = useState('');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [bulkItems, setBulkItems] = useState('');

  // Output state
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [csvRow, setCsvRow] = useState('');
  const [bulkResults, setBulkResults] = useState([]);
  const [copied, setCopied] = useState('');

  const effectiveSource = source === 'other' ? customSource : source;
  const effectiveMedium = medium === 'other' ? customMedium : medium;

  const loadLinks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.listCampaignLinks({ limit: 50 });
      setLinks(res.data.links);
      setTotal(res.data.total);
    } catch (err) {
      setError('Failed to load campaign links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const clearOutputs = () => {
    setGeneratedUrl('');
    setQrDataUrl('');
    setCsvRow('');
    setBulkResults([]);
    setError('');
    setSuccess('');
  };

  const generateQR = async (url, size = 300) => {
    return await QRCode.toDataURL(url, { width: size, margin: 2 });
  };

  const generateQRBuffer = async (url, size = 300) => {
    const canvas = document.createElement('canvas');
    await QRCode.toCanvas(canvas, url, { width: size, margin: 2 });
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  };

  const handleCopy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const downloadQR = async (url, filename) => {
    const blob = await generateQRBuffer(url);
    saveAs(blob, filename);
  };

  const getQrFilename = (camp, cont) => {
    if (cont) return `${sanitize(camp)}_${sanitize(cont)}_qr.png`;
    return `${sanitize(camp)}_qr.png`;
  };

  // SINGLE MODE
  const handleGenerateSingle = async () => {
    if (!baseUrl || !effectiveSource || !effectiveMedium || !campaign) {
      setError('Please fill in all required fields');
      return;
    }
    clearOutputs();
    setGenerating(true);
    try {
      const res = await adminAPI.createCampaignLink({
        base_url: baseUrl,
        source: effectiveSource,
        medium: effectiveMedium,
        campaign,
        content: content || null,
      });
      const link = res.data.link;
      setGeneratedUrl(link.full_url);

      const qr = await generateQR(link.full_url);
      setQrDataUrl(qr);

      const sanitizedContent = sanitize(content);
      const qrFile = getQrFilename(campaign, content);
      if (sanitizedContent) {
        setCsvRow(`content,qr_filename\n${sanitizedContent},${qrFile}`);
      }

      setSuccess('Link generated');
      loadLinks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate link');
    } finally {
      setGenerating(false);
    }
  };

  // BULK MODE
  const handleGenerateBulk = async () => {
    if (!baseUrl || !effectiveSource || !effectiveMedium || !campaign) {
      setError('Please fill in all required fields');
      return;
    }
    const lines = bulkItems.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) {
      setError('Enter at least one location/variant');
      return;
    }
    clearOutputs();
    setGenerating(true);
    try {
      const res = await adminAPI.createBulkCampaignLinks({
        base_url: baseUrl,
        source: effectiveSource,
        medium: effectiveMedium,
        campaign,
        items: lines,
      });

      const results = [];
      for (const link of res.data.links) {
        const qr = await generateQR(link.full_url);
        results.push({ ...link, qrDataUrl: qr });
      }
      setBulkResults(results);

      const dupes = res.data.duplicates || 0;
      setSuccess(`Generated ${results.length} links${dupes > 0 ? ` (${dupes} already existed)` : ''}`);
      loadLinks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate links');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadZip = async () => {
    const zip = new JSZip();
    for (const r of bulkResults) {
      const blob = await generateQRBuffer(r.full_url);
      const filename = getQrFilename(r.campaign, r.content);
      zip.file(filename, blob);
    }
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `${sanitize(campaign)}_qr_codes.zip`);
  };

  const handleDownloadCSV = () => {
    let csv = 'content,qr_filename\n';
    for (const r of bulkResults) {
      csv += `${sanitize(r.content)},${getQrFilename(r.campaign, r.content)}\n`;
    }
    const blob = new Blob([csv], { type: 'text/csv' });
    saveAs(blob, `${sanitize(campaign)}_links.csv`);
  };

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteCampaignLink(id);
      setDeleteConfirm(null);
      setSuccess('Link deleted');
      loadLinks();
    } catch (err) {
      setError('Failed to delete link');
    }
  };

  const handleRowClick = (link) => {
    setBaseUrl(link.base_url);
    setSource(SOURCE_PRESETS.includes(link.source) ? link.source : 'other');
    if (!SOURCE_PRESETS.includes(link.source)) setCustomSource(link.source);
    setMedium(MEDIUM_PRESETS.includes(link.medium) ? link.medium : 'other');
    if (!MEDIUM_PRESETS.includes(link.medium)) setCustomMedium(link.medium);
    setCampaign(link.campaign);
    setContent(link.content || '');
    setMode('single');
    clearOutputs();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} mb={3}>Campaign Links & QR Codes</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Mode Toggle */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, v) => { if (v) { setMode(v); clearOutputs(); } }}
          size="small"
          sx={{ mb: 3 }}
        >
          <ToggleButton value="single">Single</ToggleButton>
          <ToggleButton value="bulk">Bulk</ToggleButton>
        </ToggleButtonGroup>

        {/* Form */}
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Base URL"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            size="small"
            fullWidth
            required
          />
          <Box display="flex" gap={2} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 160 }} required>
              <InputLabel>Source</InputLabel>
              <Select value={source} onChange={(e) => setSource(e.target.value)} label="Source">
                {SOURCE_PRESETS.map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {source === 'other' && (
              <TextField
                label="Custom Source"
                value={customSource}
                onChange={(e) => setCustomSource(e.target.value)}
                size="small"
                sx={{ minWidth: 160 }}
              />
            )}
            <FormControl size="small" sx={{ minWidth: 160 }} required>
              <InputLabel>Medium</InputLabel>
              <Select value={medium} onChange={(e) => setMedium(e.target.value)} label="Medium">
                {MEDIUM_PRESETS.map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {medium === 'other' && (
              <TextField
                label="Custom Medium"
                value={customMedium}
                onChange={(e) => setCustomMedium(e.target.value)}
                size="small"
                sx={{ minWidth: 160 }}
              />
            )}
          </Box>
          <TextField
            label="Campaign"
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            size="small"
            fullWidth
            required
            placeholder="e.g., nursing_homes_march"
          />

          {mode === 'single' ? (
            <>
              <TextField
                label="Content (optional)"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                size="small"
                fullWidth
                placeholder="Location name, ad variant, A/B test, etc."
              />
              <Button
                variant="contained"
                onClick={handleGenerateSingle}
                disabled={generating}
                startIcon={generating ? <CircularProgress size={18} /> : <LinkIcon />}
              >
                Generate
              </Button>
            </>
          ) : (
            <>
              <TextField
                label="Locations / Variants (one per line)"
                value={bulkItems}
                onChange={(e) => setBulkItems(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={4}
                maxRows={12}
                required
                placeholder={'Sunrise Manor\nShady Pines\nOak Ridge'}
              />
              <Button
                variant="contained"
                onClick={handleGenerateBulk}
                disabled={generating}
                startIcon={generating ? <CircularProgress size={18} /> : <LinkIcon />}
              >
                {generating
                  ? `Generating ${bulkItems.split('\n').filter(l => l.trim()).length} links...`
                  : 'Generate All'}
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Single Output */}
      {mode === 'single' && generatedUrl && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} mb={2}>Generated Output</Typography>

          <Box mb={2}>
            <Typography variant="caption" color="text.secondary">Generated URL</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                value={generatedUrl}
                size="small"
                fullWidth
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
              <Tooltip title={copied === 'url' ? 'Copied!' : 'Copy'}>
                <IconButton onClick={() => handleCopy(generatedUrl, 'url')} color={copied === 'url' ? 'success' : 'default'}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box display="flex" gap={4} flexWrap="wrap" alignItems="flex-start">
            {qrDataUrl && (
              <Box>
                <Typography variant="caption" color="text.secondary">QR Code</Typography>
                <Box mt={0.5}>
                  <img src={qrDataUrl} alt="QR Code" style={{ width: 200, height: 200 }} />
                </Box>
                <Button
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => downloadQR(generatedUrl, getQrFilename(campaign, content))}
                  sx={{ mt: 1 }}
                >
                  Download PNG
                </Button>
              </Box>
            )}

            {csvRow && (
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary">CSV Row (for Canva)</Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <TextField
                    value={csvRow}
                    size="small"
                    fullWidth
                    multiline
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                  />
                  <Tooltip title={copied === 'csv' ? 'Copied!' : 'Copy'}>
                    <IconButton onClick={() => handleCopy(csvRow, 'csv')} color={copied === 'csv' ? 'success' : 'default'}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Bulk Output */}
      {mode === 'bulk' && bulkResults.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2" fontWeight={600}>
              Generated {bulkResults.length} Links
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadZip}>
                Download ZIP (QR Codes)
              </Button>
              <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadCSV}>
                Download CSV
              </Button>
            </Box>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Content</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>QR</TableCell>
                <TableCell align="right">Copy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bulkResults.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.content}</TableCell>
                  <TableCell>
                    <Tooltip title={r.full_url}>
                      <Typography variant="body2" fontFamily="monospace" fontSize={12} noWrap sx={{ maxWidth: 350 }}>
                        {r.full_url}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <img src={r.qrDataUrl} alt="QR" style={{ width: 40, height: 40 }} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={copied === r.id ? 'Copied!' : 'Copy URL'}>
                      <IconButton size="small" onClick={() => handleCopy(r.full_url, r.id)} color={copied === r.id ? 'success' : 'default'}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} mb={2}>
          History {total > 0 && `(${total})`}
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
        ) : links.length === 0 ? (
          <Typography color="text.secondary" py={3} textAlign="center">
            No campaign links yet. Generate your first one above!
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Medium</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {links.map((link) => (
                  <TableRow
                    key={link.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(link)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(link.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </TableCell>
                    <TableCell>{link.source}</TableCell>
                    <TableCell>{link.medium}</TableCell>
                    <TableCell>{link.campaign}</TableCell>
                    <TableCell>{link.content || '—'}</TableCell>
                    <TableCell>
                      <Tooltip title={link.full_url}>
                        <Typography variant="body2" fontFamily="monospace" fontSize={12} noWrap sx={{ maxWidth: 250 }}>
                          {link.full_url}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Copy URL">
                        <IconButton size="small" onClick={() => handleCopy(link.full_url, link.id)} color={copied === link.id ? 'success' : 'default'}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download QR">
                        <IconButton size="small" onClick={() => downloadQR(link.full_url, getQrFilename(link.campaign, link.content))}>
                          <QrIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteConfirm(link)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Campaign Link?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete the link for <strong>{deleteConfirm?.campaign}</strong>
            {deleteConfirm?.content ? ` / ${deleteConfirm.content}` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm.id)}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CampaignLinks;
